import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Table,
  Typography
} from "antd";
import { SortOrder } from "antd/lib/table/interface";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { ProductChangeSource } from "../../../constants/productChangeSource";
import { CompanyEnabledLog } from "../../../models/company-enabled-log-interface";
import { CustomerServiceQueries } from "../../../queries/customer-service-queries";

const { Title } = Typography;

type Props = {
  numDays: number;
};

const CPQAccessTab: React.FC<Props> = ({
  numDays
}) => {
  const history = useHistory();
  const [search, setSearch] = useState<string|null>(null);
  const [filteredData, setFilteredData] = useState<CompanyEnabledLog[]|null>(null);
  const useGetAccessLogs = CustomerServiceQueries.useCompanyAccessLogs(numDays);
  
  useEffect(() => {
    if(!!search) {
      const searchLower = search.toLocaleLowerCase();
      const data = useGetAccessLogs.data?.filter(x => x.companyName?.toLocaleLowerCase().includes(searchLower) ||
        x.reason?.toLocaleLowerCase().includes(searchLower) ||
        x.userName?.toLocaleLowerCase().includes(searchLower));
      setFilteredData(data ?? []);
    } else {
      setFilteredData(null);
    }
  }, [search])
  
  const columns = [
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      render: (companyName: string, record: CompanyEnabledLog) => {
        return (
          <Button
            type="link"
            href={`/admin/company/${record.companyId}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/admin/company/${record.companyId}`);
            }}
          >
            {companyName}
          </Button>
        );
      },
      sorter: {
        compare: (a: CompanyEnabledLog, b: CompanyEnabledLog) => a.companyName.localeCompare(b.companyName)
      }
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled: boolean) => {
        return (enabled ? "Enabled" : "Disabled")
      },
      filters: [{ text: "Enabled", value: true }, { text: "Disabled", value: false }],
      onFilter: (value: boolean, record: CompanyEnabledLog) => {
        return record.enabled === value;
      }
    },
    {
      title: "Source",
      dataIndex: "sourceId",
      key: "source",
      render: (sourceId: number) => {
        return Object.keys(ProductChangeSource).find(key => ProductChangeSource[key] === sourceId)?.replace(/([a-z])([A-Z])/g, `$1 $2`);
      },
      filters: [...Object.values(ProductChangeSource).filter(x => isNaN(x)).map((value, index) => {
        return { text: value.replace(/([a-z])([A-Z])/g, `$1 $2`), value: index + 1 }
      })],
      onFilter: (value: number, record: CompanyEnabledLog) => {
        return record.sourceId === value;
      }
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: "User",
      dataIndex: "userName",
      key: "userName",
      render: (userName: string, record: CompanyEnabledLog) => {
        return (
          <Button
            type="link"
            href={`/admin/users/manage/edit/${record.userId}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/admin/users/manage/edit/${record.userId}`);
            }}
          >
            {userName}
          </Button>
        );
      },
      sorter: {
        compare: (a: CompanyEnabledLog, b: CompanyEnabledLog) => (a.userName ?? "").localeCompare(b.userName ?? "")
      }
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => {
        return new Date(date + "Z").toLocaleString();
      },
      sorter: {
        compare: (a, b) => {
            let d1 = a.date ?? new Date("01/01/1000");
            let d2 = b.date ?? new Date("01/01/1000");
            return moment(d1).diff(moment(d2));
        }
      },
      defaultSortOrder: 'descend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
    },
  ];

  return (
    <Card style={{ width: "100%" }}>
      <Row gutter={[16, 16]}>
        <Col flex={"auto"}>
        </Col>
        <Col>
          <Input
            placeholder="Search Logs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col span={24}>
          <Table
            className="full-width"
            rowKey={(data) => data.id}
            dataSource={filteredData ?? useGetAccessLogs.data ?? []}
            columns={columns}
            loading={useGetAccessLogs.isLoading}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default CPQAccessTab;
